import { EmptySearchDoq } from './DoqDisplays';
import {
  APIComponentAttributes,
  APIReferenceAttributes,
  ArdoqId,
  ViewpointDetailEntry,
} from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { getAllReferences, ReferenceFilterOptions } from './ReferenceFilter';
import { isTruthy } from '@ardoq/common-helpers';
import {
  DatasourceTable,
  ExpandableTableProvider,
  newTableTheme,
} from '@ardoq/table-legacy';
import { colors, spacing } from '@ardoq/design-tokens';
import styled from 'styled-components';
import {
  getReferenceColumns,
  isReferenceAttributeRow,
  type ReferenceColumnLabels,
} from './referenceColumns';
import { getReferenceTypeName } from '../utils';
import { LilInfoDoq, DoqType } from '@ardoq/doq';
import { PaginationBar, PaginationController } from '@ardoq/pagination';
import {
  getCurrentLocale,
  localeCompare,
  localeIncludesLowercase,
} from '@ardoq/locale';
import { ReactNode, useState } from 'react';
import { Box, Stack } from '@ardoq/layout';
import { Skeleton, SkeletonLoader } from '@ardoq/loader';

const SKELETON_ROW_COUNT = 5;

const ReferencesSkeleton = () => (
  <SkeletonLoader message="Loading references">
    <Stack gap="small">
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <Skeleton key={index} height={spacing.s32} delay={index * 0.1} />
      ))}
    </Stack>
  </SkeletonLoader>
);

const Row = styled.tr`
  background: ${colors.surfaceDefault};
`;

const RowWithHover = styled.tr`
  background: ${colors.surfaceDefault};
  &:hover {
    background: ${colors.surfaceHover};
  }
`;

export const getIncomingReferences = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  return scopeData.references.filter(ref => {
    return ref.virtual
      ? ref.source === componentId
      : ref.target === componentId;
  });
};

export const getOutgoingReferences = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  return scopeData.references.filter(ref => {
    return ref.virtual
      ? ref.target === componentId
      : ref.source === componentId;
  });
};

const filterReferencesByType = (
  references: APIReferenceAttributes[],
  scopeData: EnhancedScopeData,
  typeFilters: Set<string>,
  parentChildReferenceName?: string
) => {
  return references.filter(ref => {
    const referenceTypeName = getReferenceTypeName(
      scopeData,
      ref,
      parentChildReferenceName
    );
    return referenceTypeName && typeFilters.has(referenceTypeName);
  });
};

export const mapReferenceToDataSourceRow = (
  references: APIReferenceAttributes[],
  scopeData: EnhancedScopeData,
  detailEntries?: ViewpointDetailEntry[],
  parentChildReferenceName?: string
) => {
  return references
    .map(reference => {
      const referenceTypeName = getReferenceTypeName(
        scopeData,
        reference,
        parentChildReferenceName
      );
      if (!referenceTypeName) return null;

      return {
        _id: reference._id,
        referenceTypeName,
        sourceName: scopeData.componentsById[reference.source].name,
        targetName: scopeData.componentsById[reference.target].name,
        children: [
          {
            reference,
            isExpandableRow: true,
            detailEntries,
            referenceTypeName,
          },
        ],
      };
    })
    .filter(isTruthy);
};

export const getRelevantReferences = (
  allReferences: APIReferenceAttributes[],
  scopeData: EnhancedScopeData,
  componentId: ArdoqId,
  filters: ReferenceFilterOptions,
  searchTerm?: string,
  parentChildReferenceName?: string
) => {
  const locale = getCurrentLocale();

  const referencesFilteredByDirection =
    filters.direction === 'incoming'
      ? getIncomingReferences(scopeData, componentId)
      : filters.direction === 'outgoing'
        ? getOutgoingReferences(scopeData, componentId)
        : allReferences;

  const referencesFilteredByDirectionAndType = filters.types.size
    ? filterReferencesByType(
        referencesFilteredByDirection,
        scopeData,
        filters.types,
        parentChildReferenceName
      )
    : referencesFilteredByDirection;

  if (!searchTerm) return referencesFilteredByDirectionAndType;

  return referencesFilteredByDirection.filter(reference => {
    const sourceComponent = scopeData.componentsById[reference.source];
    const targetComponent = scopeData.componentsById[reference.target];

    return (
      localeIncludesLowercase(sourceComponent.name, searchTerm, locale) ||
      localeIncludesLowercase(targetComponent.name, searchTerm, locale)
    );
  });
};

type ReferencesProps = {
  component: APIComponentAttributes;
  scopeData: EnhancedScopeData;
  filters: ReferenceFilterOptions;
  detailEntries?: ViewpointDetailEntry[];
  searchTerm?: string;
  onComponentClick?: (componentId: ArdoqId) => void;
  isLoading?: boolean;
  emptyStateTitle?: string;
  emptyStateMessage?: ReactNode;
  emptySearchContentType?: string;
  emptySearchTitle?: string;
  emptySearchMessage?: ReactNode;
  columnLabels?: ReferenceColumnLabels;
  parentChildReferenceName?: string;
};

const References = ({
  component,
  scopeData,
  filters,
  detailEntries,
  searchTerm,
  onComponentClick,
  isLoading,
  emptyStateTitle = 'Nothing to show',
  emptyStateMessage = (
    <>
      <div>Looks like there are no references in scope for this component.</div>
      <div>
        If you think this is a mistake, please contact your administrator.
      </div>
    </>
  ),
  emptySearchContentType = 'references',
  emptySearchTitle,
  emptySearchMessage,
  columnLabels,
  parentChildReferenceName,
}: ReferencesProps) => {
  const [perPage, setPerPage] = useState(25);
  const allReferences = getAllReferences(scopeData, component._id);

  if (!allReferences.length && isLoading) {
    return <ReferencesSkeleton />;
  }

  if (!allReferences.length) {
    return (
      <LilInfoDoq
        doqType={DoqType.SIMPLE}
        message={emptyStateMessage}
        title={emptyStateTitle}
      />
    );
  }

  const relevantReferences = getRelevantReferences(
    allReferences,
    scopeData,
    component._id,
    filters,
    searchTerm,
    parentChildReferenceName
  );

  const locale = getCurrentLocale();

  const data = mapReferenceToDataSourceRow(
    relevantReferences,
    scopeData,
    detailEntries,
    parentChildReferenceName
  ).sort((a, b) =>
    localeCompare(a.referenceTypeName, b.referenceTypeName, locale)
  );
  return (
    <PaginationController
      dataSource={data}
      perPage={perPage}
      render={({
        currentPageDataSource,
        currentPageNumber,
        totalResults,
        perPage,
        sortById,
        sortOrder,
        sortBy,
        onPageSelect,
      }) => {
        return (
          <ExpandableTableProvider
            dataSource={currentPageDataSource}
            idIndex="_id"
            render={({
              dataSource,
              expandedFoldersIds: expandedRowIds,
              expandFolder: expandRow,
            }) => (
              <>
                <DatasourceTable
                  loading={isLoading}
                  dataSource={dataSource}
                  columns={getReferenceColumns(
                    expandedRowIds,
                    sortById,
                    sortOrder,
                    expandRow,
                    sortBy,
                    scopeData,
                    component._id,
                    onComponentClick,
                    columnLabels
                  )}
                  components={newTableTheme}
                  getRowComponent={row =>
                    isReferenceAttributeRow(row) ? Row : RowWithHover
                  }
                  renderEmptyTable={() => (
                    <EmptySearchDoq
                      contentType={emptySearchContentType}
                      title={emptySearchTitle}
                      message={emptySearchMessage}
                    />
                  )}
                />
                <Box paddingX="small">
                  <PaginationBar
                    currentPageNumber={currentPageNumber}
                    perPage={perPage}
                    totalResults={totalResults}
                    onChange={({ currentPageNumber, perPage }) => {
                      setPerPage(perPage);
                      onPageSelect(currentPageNumber);
                    }}
                  />
                </Box>
              </>
            )}
          />
        );
      }}
    />
  );
};

export { References };
